import React from "react";
import { Dropdown, Message } from "semantic-ui-react";
import { useHistory } from "react-router-dom";
import { Table } from "@redriver/cinnamon";
import { withPermissions } from "components/auth";
import { Actions, Targets } from "constants/permissions";
import { updateUserStatus } from "features/Admin/ListUsers/actions";
import { updateUserMfa } from "./actions";
import SendPasswordReset from "./SendPasswordReset";
import ResendVerification from "./ResendVerification";
import ResetUserMfa from "./ResetUserMfa";
import EditUser from "./EditUser";
import { UserRoutes } from "constants/routes";
import ActionsModal from "components/modals/ActionsModal";
import { TableActionMenu } from "components/misc";

const UserActions = ({
  user,
  onRefresh,
  hasPermission,
  children,
  hideView = false,
  className,
  itemIndex,
  visibleRowCount,
  ...props
}) => {
  const history = useHistory();
  const { isDisabled, isVerified, signsInWithMicrosoft } = user;
  const hasEditPermission = hasPermission(Targets.User, Actions.Edit);
  // MFA doesn't apply to SSO accounts at all - their login flow never
  // consults the MFA enable flag or an existing registration, so neither
  // action means anything for them (and a stale registration can linger
  // from before they switched to SSO, so this can't rely on mfaEnrolled alone)
  const isMfaApplicable = !signsInWithMicrosoft;
  const ssoEnabled = signsInWithMicrosoft;

  // the value this control would submit if clicked - used both to pick the
  // label/direction and to detect whether it would override the organisation's setting
  const targetEnforceMfa = !user.enforceMfa;
  const overridesOrganisation =
    !!user.organisationId && user.organisationEnforceMfa !== targetEnforceMfa;

  const mfaWarning = overridesOrganisation ? (
    <Message
      warning
      style={{ marginTop: "1rem" }}
      header="Please Note"
      content={`This user's organisation currently ${
        user.organisationEnforceMfa ? "enforces" : "does not enforce"
      } MFA for its users. If you go ahead, the organisational MFA settings will be overridden for this user, are you sure you wish to continue?`}
    />
  ) : undefined;

  const access = {
    canEdit: hasEditPermission,
    canActivate: isDisabled && hasEditPermission,
    canDeactivate: !isDisabled && hasEditPermission,
    canSendPasswordReset: !isDisabled && isVerified && hasEditPermission,
    canResendVerification: !isDisabled && !isVerified && hasEditPermission,
    canResetMfa: isMfaApplicable && user.mfaEnrolled && hasEditPermission,
    canUpdateMfa: isMfaApplicable && hasEditPermission,
  };

  if (!Object.values(access).some((x) => x)) return null;

  return (
    <TableActionMenu
      className={className}
      {...props}
      itemIndex={itemIndex}
      visibleRowCount={visibleRowCount}
    >
      {!hideView && (
        <Table.MenuItem onClick={() => history.push(UserRoutes.User(user.id))}>
          View User
        </Table.MenuItem>
      )}
      {access.canEdit && (
        <EditUser as={Table.MenuItem} onUpdated={onRefresh} userId={user.id}>
          Edit User
        </EditUser>
      )}
      {access.canEdit && <Dropdown.Divider />}
      {access.canSendPasswordReset && !ssoEnabled && (
        <SendPasswordReset
          user={user}
          as={Table.MenuItem}
          submitParams={{ selected: [user.id] }}
        >
          Send Password Reset
        </SendPasswordReset>
      )}
      {access.canResendVerification && (
        <ResendVerification user={user} as={Table.MenuItem}>
          Resend Verification
        </ResendVerification>
      )}
      {access.canResetMfa && (
        <ResetUserMfa
          user={user}
          as={Table.MenuItem}
          submitParams={{ selected: [user.id] }}
          onReset={onRefresh}
        >
          Reset Multi-Factor Authentication
        </ResetUserMfa>
      )}
      {
        <ActionsModal
          as={Table.MenuItem}
          submitAction={updateUserStatus}
          id={user.id}
          name={`${user.firstName} ${user.lastName}`}
          onRefresh={onRefresh}
          actionName={!user.isDisabled ? "lock out" : "activate"}
          header={!user.isDisabled ? "Lock Out User" : "Activate User"}
          submitParams={{
            isDisabled: !user.isDisabled,
            selected: [user.id],
          }}
        >
          {access.canActivate ? "Activate Account" : "Lock Out"}
        </ActionsModal>
      }
      {access.canUpdateMfa && (
        <ActionsModal
          as={Table.MenuItem}
          submitAction={updateUserMfa}
          id={user.id}
          name={`${user.firstName} ${user.lastName}`}
          onRefresh={onRefresh}
          actionName={
            targetEnforceMfa
              ? "enforce multi-factor authentication for"
              : "disable multi-factor authentication enforcement for"
          }
          header={targetEnforceMfa ? "Enforce MFA" : "Disable MFA Enforcement"}
          warning={mfaWarning}
          submitParams={{
            enforceMfa: targetEnforceMfa,
            selected: [user.id],
          }}
        >
          {targetEnforceMfa ? "Enforce MFA" : "Disable MFA Enforcement"}
        </ActionsModal>
      )}
      {children}
    </TableActionMenu>
  );
};

export default withPermissions(UserActions);
